import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import {
  getPartyRatesByParty,
  importPartyRate,
} from "app/redux/slice/partyRateSlice";
import { downloadPartyRateImportSample } from "app/helpers/csvExport";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED = /\.(xlsx|xls|csv)$/i;
const MAX_ERRORS_SHOWN = 10;

const SummaryTile = ({ label, value, color }) => (
  <Grid item xs={6} sm={2.4}>
    <Box
      sx={{
        p: 1.5,
        textAlign: "center",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Typography variant="h3" mb={0} color={color}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={0}>
        {label}
      </Typography>
    </Box>
  </Grid>
);

// The parent refreshes the party rate list in its own onClose handler, not here:
// the party rate page swaps the whole page for a <Loader /> while the list
// reloads, which unmounts this dialog and would wipe the summary before it
// could be read.
const ImportPartyRate = ({ onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const productData = useSelector((state) => state?.product);
  const supplierData = useSelector((state) => state?.supplier);

  const [supplier, setSupplier] = useState(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [gst, setGst] = useState("");
  const [supplierError, setSupplierError] = useState("");

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  // Mobile number and GST default to whatever is on the party record, exactly
  // like the Add Party Rate form, but stay editable.
  const handleSupplierChange = (event, value) => {
    setSupplier(value);
    setSupplierError("");
    setGst(value?.gst ? value.gst.trim().toUpperCase() : "");
    setMobileNumber(value?.mobileNumber ? value.mobileNumber.trim() : "");
  };

  const handleSelect = (event) => {
    const picked = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (!picked) return;

    if (!ALLOWED.test(picked.name)) {
      setFileError("Please choose an Excel (.xlsx, .xls) or CSV file.");
      setFile(null);
      return;
    }
    if (picked.size > MAX_FILE_SIZE) {
      setFileError("File size should be less than 5MB.");
      setFile(null);
      return;
    }

    setFileError("");
    setFile(picked);
  };

  const handleDownloadSample = async () => {
    setDownloading(true);
    try {
      let currentRates = {};

      // With a party chosen the sheet is pre-filled with that party's live
      // rates, so rows the user does not touch import as "no change" rather
      // than overwriting them with the product default.
      if (supplier?._id) {
        const response = await dispatch(
          getPartyRatesByParty(supplier._id),
        ).unwrap();

        const rates = response?.data?.data?.supplierRateList ?? [];
        currentRates = rates.reduce((map, rate) => {
          const productId = rate?.product?._id || rate?.productId;
          if (productId) {
            map[productId] = rate.price;
          }
          return map;
        }, {});
      }

      downloadPartyRateImportSample(productData?.list ?? [], currentRates);
    } catch (error) {
      enqueueSnackbar("Could not build the sample file.", { variant: "error" });
    }
    setDownloading(false);
  };

  const handleImport = async () => {
    if (!supplier?._id) {
      setSupplierError("Please select a party");
      return;
    }
    if (!file) return;

    setImporting(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("supplierId", supplier._id);
    formData.append("gst", gst?.trim()?.toUpperCase() || "");
    formData.append("mobileNumber", mobileNumber?.trim() || "");

    const response = await dispatch(importPartyRate(formData)).unwrap();
    const isSuccess = response.status === 200;

    enqueueSnackbar(response.data?.responseMessage || "Import failed", {
      variant: isSuccess ? "success" : "error",
    });

    if (isSuccess) {
      setResult(response.data?.data?.partyRateImport ?? null);
    }
    setImporting(false);
  };

  // ---- Summary view, shown once an import has run ----
  if (result) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <SummaryTile label="Total rows" value={result.totalRows ?? 0} />
          <SummaryTile
            label="Created"
            value={result.created ?? 0}
            color="success.main"
          />
          <SummaryTile
            label="Updated"
            value={result.updated ?? 0}
            color="info.main"
          />
          <SummaryTile
            label="Skipped"
            value={result.skipped ?? 0}
            color="warning.main"
          />
          <SummaryTile
            label="Errors"
            value={result.failed ?? 0}
            color="error.main"
          />
        </Grid>

        {result.errors?.length > 0 && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="h5" mb={1}>
              Rows not imported
            </Typography>
            <Box
              component="ul"
              sx={{ pl: 3, m: 0, maxHeight: 200, overflowY: "auto" }}
            >
              {result.errors.slice(0, MAX_ERRORS_SHOWN).map((message) => (
                <li key={message}>
                  <Typography variant="body2" mb={0}>
                    {message}
                  </Typography>
                </li>
              ))}
            </Box>
            {result.errors.length > MAX_ERRORS_SHOWN && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Showing first {MAX_ERRORS_SHOWN} of {result.errors.length}.
              </Typography>
            )}
          </>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <LoadingButton variant="contained" size="large" onClick={onClose}>
            Done
          </LoadingButton>
        </Stack>
      </Box>
    );
  }

  // ---- Upload view ----
  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <JumboAutoComplete
            options={supplierData?.list ?? []}
            label="Supplier*"
            size="small"
            getOptionLabel={(option) => option?.name}
            name="supplierId"
            value={supplier}
            onChange={handleSupplierChange}
            helperText={supplierError}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            sx={{ width: "100%" }}
            name="mobileNumber"
            label="Mobile Number"
            size="small"
            value={mobileNumber}
            onChange={(event) => setMobileNumber(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            sx={{ width: "100%" }}
            name="gst"
            label="GST"
            size="small"
            value={gst}
            onChange={(event) => setGst(event.target.value)}
          />
        </Grid>
      </Grid>

      <Typography variant="body1" sx={{ mb: 1 }}>
        Upload an Excel or CSV file with these columns:
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>product</strong> and <strong>price</strong> — both are required.
        Products that already have a rate for this party are updated, the rest
        are added. Allowed types: XLSX, XLS, CSV. Max size 5MB, up to 2000 rows.
      </Typography>

      <Link
        component="button"
        type="button"
        underline="always"
        disabled={downloading}
        onClick={handleDownloadSample}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2 }}
      >
        <CloudDownloadIcon fontSize="small" />
        {downloading ? "Preparing…" : "Download sample file"}
      </Link>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mt: 1, mb: 1 }}
      >
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          Choose File
          <input
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleSelect}
          />
        </Button>
        <Typography variant="body2" color="text.secondary" mb={0}>
          {file ? file.name : "No file selected"}
        </Typography>
      </Stack>

      {fileError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fileError}
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 3 }}
      >
        <LoadingButton
          variant="outlined"
          color="secondary"
          size="large"
          onClick={onClose}
          disabled={importing}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          size="large"
          onClick={handleImport}
          disabled={!file || !supplier}
          loading={importing}
          loadingIndicator={<CircularProgress size={20} />}
        >
          Import
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default ImportPartyRate;
